import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useDashboardSettings, useTheme, useModuleVisibility } from "../App";
import {
  Settings, Save, Building2, Target, Percent, Calculator, FileText, Plus,
  Upload, LayoutDashboard, GraduationCap, LayoutGrid
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import DatenImport from "./DatenImport";
import DarstellungTab from "../components/einstellungen/DarstellungTab";
import SteuervergleichTab from "../components/einstellungen/SteuervergleichTab";
import KonzessionenTab from "../components/einstellungen/KonzessionenTab";
import TutorialTab from "../components/einstellungen/TutorialTab";
import BWATab from "../components/einstellungen/BWATab";
import StandorteTab from "../components/einstellungen/StandorteTab";
import PanelsTab from "../components/einstellungen/PanelsTab";
import { invalidateLandCache } from "../hooks/useLand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Einstellungen = () => {
  const [searchParams] = useSearchParams();
  const { theme, setTheme } = useTheme();
  const { settings: dashboardSettings, updateSetting, resetSettings } = useDashboardSettings();
  const { visibility, updateVisibility, resetVisibility, applyPreset } = useModuleVisibility();

  const [unternehmen, setUnternehmen] = useState({
    firmenname: "", unternehmensform: "", inhaber_name: "", steuernummer: "",
    ust_id: "", handelsregister: "", adresse: "", plz: "", stadt: "",
    standort: "limburg", land: "DE", telefon: "", email: "", website: "", betriebsart: "",
    sitzplaetze_innen: "", sitzplaetze_aussen: "", sitzplaetze_aussen_aktiv: true,
    sitzplaetze_beschirmt: "", sitzplaetze_beschirmt_aktiv: true,
    sitzplaetze_wintergarten: "", sitzplaetze_wintergarten_aktiv: true
  });

  const [unternehmensformen, setUnternehmensformen] = useState([]);
  const [konzessionsarten, setKonzessionsarten] = useState([]);
  const [konzessionen, setKonzessionen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [unternehmensformenRes, konzessionsartenRes, unternehmensRes, konzessionenRes] = await Promise.all([
        axios.get(`${API}/unternehmensformen`),
        axios.get(`${API}/konzessionsarten`),
        axios.get(`${API}/unternehmenseinstellungen`, { withCredentials: true }),
        axios.get(`${API}/konzessionen`, { withCredentials: true })
      ]);

      const allForms = unternehmensformenRes.data;
      setUnternehmensformen(allForms);
      setKonzessionsarten(konzessionsartenRes.data);
      if (unternehmensRes.data) {
        const data = unternehmensRes.data;
        setUnternehmen({
          firmenname: data.firmenname || "", unternehmensform: data.unternehmensform || "",
          inhaber_name: data.inhaber_name || "", steuernummer: data.steuernummer || "",
          ust_id: data.ust_id || "", handelsregister: data.handelsregister || "",
          adresse: data.adresse || "", plz: data.plz || "", stadt: data.stadt || "",
          standort: data.standort || "limburg", land: data.land || "DE",
          telefon: data.telefon || "",
          email: data.email || "", website: data.website || "",
          betriebsart: data.betriebsart || "",
          sitzplaetze_innen: data.sitzplaetze_innen?.toString() || "",
          sitzplaetze_aussen: data.sitzplaetze_aussen?.toString() || "",
          sitzplaetze_aussen_aktiv: data.sitzplaetze_aussen_aktiv !== false,
          sitzplaetze_beschirmt: data.sitzplaetze_beschirmt?.toString() || "",
          sitzplaetze_beschirmt_aktiv: data.sitzplaetze_beschirmt_aktiv !== false,
          sitzplaetze_wintergarten: data.sitzplaetze_wintergarten?.toString() || "",
          sitzplaetze_wintergarten_aktiv: data.sitzplaetze_wintergarten_aktiv !== false
        });
      }
      setKonzessionen(konzessionenRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUnternehmen = async () => {
    setSaving(true);
    try {
      const payload = {
        ...unternehmen,
        sitzplaetze_innen: unternehmen.sitzplaetze_innen ? parseInt(unternehmen.sitzplaetze_innen) : null,
        sitzplaetze_aussen: unternehmen.sitzplaetze_aussen ? parseInt(unternehmen.sitzplaetze_aussen) : null,
        sitzplaetze_aussen_aktiv: unternehmen.sitzplaetze_aussen_aktiv,
        sitzplaetze_beschirmt: unternehmen.sitzplaetze_beschirmt ? parseInt(unternehmen.sitzplaetze_beschirmt) : null,
        sitzplaetze_beschirmt_aktiv: unternehmen.sitzplaetze_beschirmt_aktiv,
        sitzplaetze_wintergarten: unternehmen.sitzplaetze_wintergarten ? parseInt(unternehmen.sitzplaetze_wintergarten) : null,
        sitzplaetze_wintergarten_aktiv: unternehmen.sitzplaetze_wintergarten_aktiv
      };
      await axios.post(`${API}/unternehmenseinstellungen`, payload, { withCredentials: true });
      invalidateLandCache();
      toast.success("Einstellungen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const selectedUnternehmensform = unternehmensformen.find(u => u.code === unternehmen.unternehmensform);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#858585]">Laden...</div></div>;
  }

  return (
    <div data-testid="einstellungen-page">
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Einstellungen</h1>
            <p className="page-subtitle">Unternehmen, Konzessionen & rechtliche Angaben</p>
          </div>
          <Button onClick={handleSaveUnternehmen} disabled={saving} className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm">
            <Save size={16} className="mr-2" />
            {saving ? "Speichern..." : "Speichern"}
          </Button>
        </div>
      </header>

      <div className="px-8 py-4">
        <Tabs defaultValue={searchParams.get("tab") || "unternehmen"} className="w-full">
          <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 mb-6 flex-wrap">
            <TabsTrigger value="unternehmen" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <Building2 size={14} className="mr-1" />Unternehmen
            </TabsTrigger>
            <TabsTrigger value="standorte" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <Building2 size={14} className="mr-1" />Standorte
            </TabsTrigger>
            <TabsTrigger value="darstellung" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <LayoutDashboard size={14} className="mr-1" />Darstellung
            </TabsTrigger>
            <TabsTrigger value="panels" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <LayoutGrid size={14} className="mr-1" />Panels
            </TabsTrigger>
            <TabsTrigger value="konzessionen" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <FileText size={14} className="mr-1" />Konzessionen
            </TabsTrigger>
            <TabsTrigger value="steuervergleich" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <Calculator size={14} className="mr-1" />Steuervergleich
            </TabsTrigger>
            <TabsTrigger value="bwa" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <FileText size={14} className="mr-1" />BWA
            </TabsTrigger>
            <TabsTrigger value="datenimport" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <Upload size={14} className="mr-1" />Datenimport
            </TabsTrigger>
            <TabsTrigger value="tutorial" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 text-xs">
              <GraduationCap size={14} className="mr-1" />Gian-Luca
            </TabsTrigger>
          </TabsList>

          {/* Darstellung Tab */}
          <TabsContent value="darstellung">
            <DarstellungTab
              dashboardSettings={dashboardSettings}
              updateSetting={updateSetting}
              resetSettings={resetSettings}
              visibility={visibility}
              updateVisibility={updateVisibility}
              resetVisibility={resetVisibility}
              applyPreset={applyPreset}
            />
          </TabsContent>

          {/* Panels Tab */}
          <TabsContent value="panels">
            <PanelsTab />
          </TabsContent>

          {/* Unternehmen Tab */}
          <TabsContent value="unternehmen">
            <div className="space-y-6">
              {/* Unternehmensform */}
              <div className="bg-white border border-stone-200 p-5">
                <h3 className="font-display text-base font-semibold text-[#3A4D39] mb-4">Unternehmensform & Land</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Land</Label>
                    <Select value={unternehmen.land || "DE"} onValueChange={(v) => {
                      const defaultUF = v === "AT" ? "AT_EU" : "EU";
                      const defaultStandort = v === "AT" ? "wien" : "limburg";
                      setUnternehmen({...unternehmen, land: v, unternehmensform: defaultUF, standort: defaultStandort});
                    }}>
                      <SelectTrigger className="border-stone-300 rounded-sm" data-testid="select-land"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DE">Deutschland</SelectItem>
                        <SelectItem value="AT">Österreich</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Rechtsform</Label>
                    <Select value={unternehmen.unternehmensform} onValueChange={(v) => setUnternehmen({...unternehmen, unternehmensform: v})}>
                      <SelectTrigger className="border-stone-300 rounded-sm"><SelectValue placeholder="Unternehmensform waehlen" /></SelectTrigger>
                      <SelectContent>
                        {unternehmensformen
                          .filter(u => {
                            if (unternehmen.land === "AT") return u.code?.startsWith("AT_") || u.land === "AT";
                            return !u.code?.startsWith("AT_") && u.code !== 'FREIB';
                          })
                          .map((u) => (
                            <SelectItem key={u.code} value={u.code}>{u.name}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Firmenname</Label>
                    <Input value={unternehmen.firmenname} onChange={(e) => setUnternehmen({...unternehmen, firmenname: e.target.value})} placeholder="z.B. Cocktailbar Sunshine GmbH" className="border-stone-300 rounded-sm" />
                  </div>
                </div>
                {unternehmen.land === "AT" && (
                  <div className="mt-4 bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800">
                    <strong>Oesterreich:</strong> USt 10% Speisen (ermaessigt), 20% Getraenke (Normalsteuersatz). Keine Gewerbesteuer, stattdessen Kommunalsteuer 3% auf Bruttolohnsumme.
                  </div>
                )}
                {selectedUnternehmensform && (
                  <div className="mt-4 bg-stone-50 p-3 text-xs">
                    <p className="text-[#5C5C5C]"><strong>Haftung:</strong> {selectedUnternehmensform.haftung}</p>
                    {selectedUnternehmensform.mindestkapital && <p className="text-[#5C5C5C]"><strong>Mindestkapital:</strong> {formatCurrency(selectedUnternehmensform.mindestkapital)}</p>}
                    {selectedUnternehmensform.hinweis && <p className="text-amber-700 mt-1"><strong>Hinweis:</strong> {selectedUnternehmensform.hinweis}</p>}
                  </div>
                )}
              </div>

              {/* Stammdaten */}
              <div className="bg-white border border-stone-200 p-5">
                <h3 className="font-display text-base font-semibold text-[#3A4D39] mb-4">Stammdaten</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Inhaber / Geschäftsführer</Label>
                    <Input value={unternehmen.inhaber_name} onChange={(e) => setUnternehmen({...unternehmen, inhaber_name: e.target.value})} placeholder="Max Mustermann" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Steuernummer</Label>
                    <Input value={unternehmen.steuernummer} onChange={(e) => setUnternehmen({...unternehmen, steuernummer: e.target.value})} placeholder="123/456/78901" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">USt-IdNr.</Label>
                    <Input value={unternehmen.ust_id} onChange={(e) => setUnternehmen({...unternehmen, ust_id: e.target.value})} placeholder={unternehmen.land === "AT" ? "ATU12345678" : "DE123456789"} className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Handelsregister</Label>
                    <Input value={unternehmen.handelsregister} onChange={(e) => setUnternehmen({...unternehmen, handelsregister: e.target.value})} placeholder="HRB 12345, AG München" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Telefon</Label>
                    <Input value={unternehmen.telefon} onChange={(e) => setUnternehmen({...unternehmen, telefon: e.target.value})} placeholder="089 12345678" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">E-Mail</Label>
                    <Input value={unternehmen.email} onChange={(e) => setUnternehmen({...unternehmen, email: e.target.value})} placeholder="info@cocktailbar.de" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="col-span-full space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Adresse</Label>
                    <Input value={unternehmen.adresse} onChange={(e) => setUnternehmen({...unternehmen, adresse: e.target.value})} placeholder="Musterstraße 1" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">PLZ</Label>
                    <Input value={unternehmen.plz} onChange={(e) => setUnternehmen({...unternehmen, plz: e.target.value})} placeholder="65549" className="border-stone-300 rounded-sm font-mono" maxLength={5} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Stadt</Label>
                    <Input value={unternehmen.stadt} onChange={(e) => setUnternehmen({...unternehmen, stadt: e.target.value})} placeholder="Limburg" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Standort (fuer Wetter)</Label>
                    <Select value={unternehmen.standort || (unternehmen.land === "AT" ? "wien" : "limburg")} onValueChange={(v) => setUnternehmen({...unternehmen, standort: v})}>
                      <SelectTrigger className="border-stone-300 rounded-sm"><SelectValue placeholder="Standort fuer Wetter" /></SelectTrigger>
                      <SelectContent>
                        {unternehmen.land === "AT" ? (
                          <>
                            {["wien","graz","linz","salzburg","innsbruck","klagenfurt","villach","wels","st_poelten","bregenz"].map(s => (
                              <SelectItem key={s} value={s}>{s === "st_poelten" ? "St. Poelten" : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                            ))}
                          </>
                        ) : (
                          <>
                            {["limburg","berlin","hamburg","muenchen","koeln","frankfurt","stuttgart","duesseldorf","dortmund","essen","bremen","dresden","hannover","nuernberg","leipzig"].map(s => (
                              <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace("ue","ue").replace("oe","oe")}</SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Wird fuer Live-Wetter auf dem Dashboard verwendet</p>
                  </div>
                </div>
              </div>

              {/* Betrieb */}
              <div className="bg-white border border-stone-200 p-5">
                <h3 className="font-display text-base font-semibold text-[#3A4D39] mb-4">Betriebsangaben</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="col-span-full space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Betriebsart</Label>
                    <Select value={unternehmen.betriebsart} onValueChange={(v) => setUnternehmen({...unternehmen, betriebsart: v})}>
                      <SelectTrigger className="border-stone-300 rounded-sm"><SelectValue placeholder="Art wählen" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bar">Bar / Cocktailbar</SelectItem>
                        <SelectItem value="restaurant">Restaurant</SelectItem>
                        <SelectItem value="cafe">Café</SelectItem>
                        <SelectItem value="bistro">Bistro</SelectItem>
                        <SelectItem value="imbiss">Imbiss</SelectItem>
                        <SelectItem value="club">Club / Diskothek</SelectItem>
                        <SelectItem value="biergarten">Biergarten</SelectItem>
                        <SelectItem value="hotel">Hotel-Restaurant</SelectItem>
                        <SelectItem value="catering">Catering</SelectItem>
                        <SelectItem value="foodtruck">Food Truck</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Sitzplätze innen</Label>
                    <Input type="number" value={unternehmen.sitzplaetze_innen} onChange={(e) => setUnternehmen({...unternehmen, sitzplaetze_innen: e.target.value})} placeholder="50" className="border-stone-300 rounded-sm font-mono" />
                  </div>
                  {[
                    { key: "aussen", label: "Sitzplätze außen" },
                    { key: "beschirmt", label: "Beschirmte Außenplätze" },
                    { key: "wintergarten", label: "Wintergarten" },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">{label}</Label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-slate-400">{unternehmen[`sitzplaetze_${key}_aktiv`] ? 'Aktiv' : 'Inaktiv'}</span>
                          <Switch
                            checked={unternehmen[`sitzplaetze_${key}_aktiv`]}
                            onCheckedChange={(checked) => setUnternehmen({...unternehmen, [`sitzplaetze_${key}_aktiv`]: checked})}
                            data-testid={`toggle-${key}`}
                          />
                        </div>
                      </div>
                      <Input type="number" value={unternehmen[`sitzplaetze_${key}`]} onChange={(e) => setUnternehmen({...unternehmen, [`sitzplaetze_${key}`]: e.target.value})} placeholder="20" className="border-stone-300 rounded-sm font-mono" disabled={!unternehmen[`sitzplaetze_${key}_aktiv`]} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Konzessionen Tab */}
          <TabsContent value="konzessionen">
            <KonzessionenTab konzessionen={konzessionen} konzessionsarten={konzessionsarten} onRefresh={fetchData} />
          </TabsContent>

          {/* Standorte Tab */}
          <TabsContent value="standorte">
            <StandorteTab />
          </TabsContent>

          {/* Steuervergleich Tab */}
          <TabsContent value="steuervergleich">
            <SteuervergleichTab unternehmensform={unternehmen.unternehmensform} land={unternehmen.land} />
          </TabsContent>

          {/* BWA Tab */}
          <TabsContent value="bwa">
            <BWATab />
          </TabsContent>

          {/* Datenimport Tab */}
          <TabsContent value="datenimport">
            <DatenImport />
          </TabsContent>

          {/* Tutorial Tab */}
          <TabsContent value="tutorial">
            <TutorialTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Einstellungen;
